import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ListTodo, AlertCircle, Calendar, User, Users, ChevronRight, CheckCircle2 } from "lucide-react";
import { tasksApi, usersApi } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import TaskDetailModal, { taskAssigneeNames } from "../TaskDetailModal";

const asLocalDay = (s) => {
  if (!s) return null;
  const d = s.length === 10 ? new Date(`${s}T00:00:00`) : new Date(s);
  return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const fmtDate = (s) => {
  const d = asLocalDay(s);
  return d ? d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";
};

/**
 * Dashboard widget: the logged-in user's OPEN tasks that are due today or overdue.
 * Clicking a task opens the shared TaskDetailModal (complete / reassign / add note).
 */
export default function MyTasksWidget() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [openTask, setOpenTask] = useState(null);
  const canManage = user?.role === "admin" || user?.role === "dispatcher";

  const reload = () =>
    tasksApi.list({ mine: true, status: "open" }).then(setTasks).catch(() => {});

  useEffect(() => {
    reload();
    if (canManage) usersApi.list().then((u) => setStaff(u.map((x) => ({ id: x.id, name: x.name || x.email })))).catch(() => {});
    const handler = () => reload();
    window.addEventListener("tasks-changed", handler);
    return () => window.removeEventListener("tasks-changed", handler);
  }, [canManage]);

  const { overdue, today } = useMemo(() => {
    const t0 = asLocalDay(new Date().toISOString());
    const o = [], td = [];
    tasks.forEach((t) => {
      const d = asLocalDay(t.dueAt);
      if (!d) return;
      const diff = Math.round((d - t0) / 86400000);
      if (diff < 0) o.push(t);
      else if (diff === 0) td.push(t);
    });
    return { overdue: o, today: td };
  }, [tasks]);

  const items = [...overdue, ...today];

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 p-5" data-testid="my-tasks-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-rose-50 flex items-center justify-center">
            <ListTodo className="h-4 w-4 text-rose-600" />
          </div>
          <div>
            <h3 className="text-[15px] font-semibold text-slate-900">My Tasks</h3>
            <p className="text-[12px] text-slate-500">Due today &amp; overdue</p>
          </div>
        </div>
        <button onClick={() => navigate("/tasks")} data-testid="my-tasks-viewall" className="text-[12px] font-semibold text-blue-600 hover:underline inline-flex items-center gap-0.5">
          All tasks <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center" data-testid="my-tasks-empty">
          <CheckCircle2 className="h-8 w-8 text-emerald-400 mb-2" />
          <div className="text-[13px] font-semibold text-slate-600">You&apos;re all caught up</div>
          <div className="text-[11.5px] text-slate-400">No tasks due today or overdue</div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="my-tasks-list">
          {overdue.length > 0 && (
            <div className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wide text-rose-600 px-0.5">
              <AlertCircle className="h-3 w-3" /> Overdue · {overdue.length}
            </div>
          )}
          {items.map((t, i) => {
            const isOverdue = i < overdue.length;
            const names = taskAssigneeNames(t);
            const showTodayHeader = isOverdue === false && i === overdue.length && today.length > 0 && overdue.length > 0;
            return (
              <React.Fragment key={t.id}>
                {showTodayHeader && (
                  <div className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wide text-blue-600 px-0.5 pt-1">
                    <Calendar className="h-3 w-3" /> Today · {today.length}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setOpenTask(t)}
                  data-testid={`my-task-${t.id}`}
                  className={`w-full text-left rounded-lg border-l-4 bg-slate-50/60 hover:bg-slate-50 px-3 py-2 transition-colors ${isOverdue ? "border-rose-400" : "border-blue-400"}`}
                >
                  <div className="text-[13px] font-semibold text-slate-900 truncate">{t.title}</div>
                  <div className="mt-0.5 flex items-center gap-3 flex-wrap text-[11px] text-slate-500">
                    {names.length > 0 && (
                      <span className="inline-flex items-center gap-1">
                        {names.length > 1 ? <Users className="h-3 w-3" /> : <User className="h-3 w-3" />}
                        {names.length <= 2 ? names.join(", ") : `${names[0]} +${names.length - 1}`}
                      </span>
                    )}
                    {t.dueAt && <span className={`inline-flex items-center gap-1 ${isOverdue ? "text-rose-600 font-semibold" : ""}`}><Calendar className="h-3 w-3" /> {fmtDate(t.dueAt)}</span>}
                  </div>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      )}

      {openTask && (
        <TaskDetailModal
          task={openTask}
          users={staff}
          canManage={canManage}
          canNote={canManage || (user && (openTask.assigneeIds || []).includes(user.id))}
          onClose={() => setOpenTask(null)}
          onUpdated={(updated) => {
            setOpenTask(updated);
            window.dispatchEvent(new Event("tasks-changed"));
          }}
        />
      )}
    </div>
  );
}
